import React, { useState, useEffect } from 'react';
import axios from 'utils/axiosInstance';
import getRootData from 'utils/getRootData';
import { UserInfo } from 'types/user_info';

import MemberNavigation from 'components/organs/member/MemberNavigation';

const VITE_BOOTPAY_JAVASCRIPT_KEY = import.meta.env.VITE_BOOTPAY_JAVASCRIPT_KEY;

interface RootData {
  userInfo: UserInfo;
}

interface Membership {
  plan: string;
  expires_at: string;
  started_at: string;
  status?: string;
  cancel_planned?: boolean;
}

interface CancellationRequest {
  status: string;
  requested_at: string;
  processed_at?: string;
  cancellation_type: string;
  cancellation_type_display?: string;
  reason?: string;
}

interface ExpiredPaidMembership {
  plan: string;
  plan_key: string;
  expires_at: string;
  activation_source?: string;
}

interface MembershipStatus {
  status: string;
  membership?: Membership;
  expired_paid_membership?: ExpiredPaidMembership;
  cancellation_request?: CancellationRequest;
  message: string;
  profile_incomplete?: boolean;
}

export default function MyPlan() {

  const [membershipStatus, setMembershipStatus] = useState<MembershipStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasActiveBillingKey, setHasActiveBillingKey] = useState(false);
  const [isMajorAdmin, setIsMajorAdmin] = useState(false);

  useEffect(() => {
    const { userInfo } = getRootData<RootData>() ?? { userInfo: null };
    //console.log('userInfo', userInfo);
    
    if (!userInfo || userInfo.id == null) {
      // 로그인하지 않은 경우 로그인 페이지로 리다이렉트
      window.location.href = '/users/sign_in?return_to=/my_plan';
      return;
    }

    const roleInfo = userInfo.roleinfo || {};
    const majorFlag = roleInfo.isMajorAdminGroup ?? roleInfo.is_major_admin_group;
    setIsMajorAdmin(Boolean(majorFlag));

    fetchMembershipStatus();
    checkBillingKeyStatus();
  }, []); // 빈 배열로 변경하여 한 번만 실행

  const fetchMembershipStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/plans/status');
      setMembershipStatus(response.data);
      setError(null);
    } catch (err: any) {
      console.error('멤버십 상태 조회 실패:', err);
      
      // // 403 에러인 경우 로그인 페이지로 리다이렉트
      // if (err.response?.status === 403 || err.response?.status === 401) {
      //   window.location.href = '/users/sign_in?return_to=/my_plan';
      //   return;
      // }
      
      setError('멤버십 상태를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleUpgrade = () => {
    window.location.href = '/select_plan';
  };

  const checkBillingKeyStatus = async () => {
    try {
      const response = await axios.get('/payment/payment_methods.json');
      if (response.data.success && response.data.data) {
        setHasActiveBillingKey(!!response.data.data.has_billing_key);
      }
    } catch (err: any) {
      // 빌링키 상태 조회 실패는 조용히 처리 (결제수단 메뉴만 숨김)
      console.log('빌링키 상태 조회 실패:', err);
      setHasActiveBillingKey(false);
    }
  };

  const handleCancelSubscription = async () => {
    // 무료 회원인지 확인 (API 로직과 동일하게)
    const isFreeMember = membershipStatus?.membership?.plan === '준회원' ||
                        membershipStatus?.membership?.plan_key === 'basic';

    const confirmMessage = isFreeMember
      ? '정말로 무료 멤버십(준회원)을 취소하시겠습니까?\n\n' +
        '- 무료 멤버십은 즉시 취소됩니다.\n' +
        '- 관리자 승인 없이 바로 처리됩니다.\n'
      : '정말로 구독을 취소신청하시겠습니까?\n\n' +
        '- 구독을 취소하면 다음 결제일부터 요금이 청구되지 않습니다.\n' +
        '- 현재 멤버십은 만료일까지 유지됩니다.\n' +
        '- (효성cms는) 관리자 체크 후 반영됩니다.\n';

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await axios.post('/api/v1/plans/cancel_subscription');
      
      if (response.data.success) {
        alert(response.data.message);
        // 상태 새로고침
        await fetchMembershipStatus();
      } else {
        alert(response.data.message || '구독 취소에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('구독 취소 실패:', err);
      const message = err.response?.data?.message || '구독 취소 중 오류가 발생했습니다.';
      alert(message);
    }
  };

  const handleWithdrawCancellation = async () => {
    if (!confirm('구독 취소 신청을 철회하시겠습니까?\n\n' +
            '- 취소 신청이 철회되면 구독이 계속 유지됩니다.\n' +
            '- 다음 결제일에 정상적으로 결제가 진행됩니다.\n\n')) {
      return;
    }

    try {
      const response = await axios.post('/api/v1/plans/withdraw_cancellation');
      
      if (response.data.success) {
        alert(response.data.message);
        // 상태 새로고침
        await fetchMembershipStatus();
      } else {
        alert(response.data.message || '구독 취소 신청 철회에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('구독 취소 신청 철회 실패:', err);
      const message = err.response?.data?.message || '구독 취소 신청 철회 중 오류가 발생했습니다.';
      alert(message);
    }
  };

  const handleHideCancellationNotice = async () => {
    try {
      const response = await axios.post('/api/v1/plans/dismiss_cancellation_notice');
      
      if (response.data.success) {
        // 상태 새로고침하여 알림이 사라지도록 함
        await fetchMembershipStatus();
      } else {
        alert(response.data.message || '알림 숨기기에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('알림 숨기기 오류:', err);
      const message = err.response?.data?.message || '알림 숨기기 중 오류가 발생했습니다.';
      alert(message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-20">
            <div className="loading loading-spinner loading-lg"></div>
            <p className="mt-4 text-lg">멤버십 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-base-200">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center py-20">
            <div className="text-error text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold mb-4">오류가 발생했습니다</h2>
            <p className="text-lg text-base-content/70 mb-6">{error}</p>
            <button 
              className="btn btn-primary"
              onClick={fetchMembershipStatus}
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="container mx-auto px-4 py-10">
        <div className="max-w-4xl mx-auto">
          {/* 회원 네비게이션 바 */}
          <MemberNavigation
            currentPage="my_plan"
            hasActiveBillingKey={hasActiveBillingKey}
            showTrustedDevicesLink={isMajorAdmin}
          />

          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">내 멤버십</h1>
            <p className="text-lg text-base-content/70">
              현재 멤버십 상태와 혜택을 확인하세요
            </p>
          </div>

          {/* 결제 실패 알림 배너 (유료 멤버십 만료 후 Basic으로 다운그레이드된 경우) */}
          {membershipStatus?.status === 'subscribed' &&
           membershipStatus.membership?.plan_key === 'basic' &&
           membershipStatus.expired_paid_membership && (
            <div className="alert alert-error mb-6">
              <div className="flex-1">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="ml-3">
                    <div className="font-bold">⚠️ 결제 실패 안내</div>
                    <div className="text-sm mt-1">
                      원래 구독하셨던 <strong>{membershipStatus.expired_paid_membership.plan}</strong> 플랜이 만료되었습니다.<br/>
                      (만료일: {formatDate(membershipStatus.expired_paid_membership.expires_at)})<br/>
                      다음 결제일에 자동으로 재시도되며, 성공 시 원래 플랜으로 복구됩니다.
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-none">
                <button
                  className="btn btn-sm btn-warning min-h-8 h-8 px-3 whitespace-nowrap"
                  onClick={() => window.location.href = '/payment/payment_methods'}
                >
                  결제수단 확인
                </button>
              </div>
            </div>
          )}

          {/* 프로필 미완성 알림 배너 */}
          {membershipStatus?.status === 'subscribed' &&
           membershipStatus.membership?.plan !== '준회원' &&
           membershipStatus.profile_incomplete
           && (
            <div className="alert alert-warning mb-6">
              <div className="flex-1">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-3">
                    <div className="font-bold">🎉 유료 멤버십 가입을 축하합니다!</div>
                    <div className="text-sm mt-1">
                      뉴스레터 발송을 위해 <strong>주소와 연락처 정보</strong>를 입력해주세요.<br/>
                      상세정보 페이지에서 우편번호, 주소, 뉴스레터 구독 설정을 완료하시면 됩니다.
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-none">
                <button
                  className="btn btn-sm btn-primary min-h-8 h-8 px-3 whitespace-nowrap"
                  onClick={() => window.location.href = '/profile/member_details'}
                >
                  정보 입력하기
                </button>
              </div>
            </div>
          )}

          {/* 멤버십 상태 카드 */}
          <div className="card bg-base-100 shadow-lg mb-8">
            <div className="card-body">
              {membershipStatus?.status === 'subscribed' && membershipStatus.membership ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="card-title text-2xl text-primary">
                        {membershipStatus.membership.plan}
                      </h2>
                      {membershipStatus.membership.cancel_planned ? (
                        <div className="badge badge-warning badge-lg mt-2">구독 취소 예정</div>
                      ) : (
                        <div className="badge badge-success badge-lg mt-2">활성</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-base-content/60">만료까지</div>
                      <div className="text-2xl font-bold">
                        {getDaysRemaining(membershipStatus.membership.expires_at)}일
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-base-200 p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">가입일</h3>
                      <p className="text-base-content/80">
                        {formatDate(membershipStatus.membership.started_at)}
                      </p>
                    </div>
                    <div className="bg-base-200 p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">만료일</h3>
                      <p className="text-base-content/80">
                        {formatDate(membershipStatus.membership.expires_at)}
                      </p>
                      {/* 준회원(Basic) 자동 연장 안내 */}
                      {membershipStatus.membership.plan_key === 'basic' && !membershipStatus.expired_paid_membership && (
                        <p className="text-xs text-base-content/50 mt-2">
                          * 준회원은 만료 시 자동으로 연장됩니다
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 구독 취소 예정 안내 */}
                  {membershipStatus.membership.cancel_planned && (
                    <div className="alert alert-warning mb-6">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <div className="ml-3">
                            <div className="font-bold">구독 취소가 예정되어 있습니다</div>
                            <div className="text-xs">
                              현재 멤버십은 <strong>{formatDate(membershipStatus.membership.expires_at)}</strong>까지 유지됩니다.<br/>
                              만료일 이후 자동으로 구독이 해지되며, 추가 결제는 발생하지 않습니다.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 구독 취소 신청 상태 표시 */}
                  {membershipStatus.cancellation_request && (() => {
                    // 고유 식별자 생성 (요청일 + 상태 + 유형 조합)
                    // DB에서 이미 필터링되므로 바로 표시
                    
                    return (
                      <div className={`alert mb-6 relative ${
                        membershipStatus.cancellation_request.status === 'processed' 
                          ? 'alert-error'
                          : membershipStatus.cancellation_request.status === 'rejected'
                          ? 'alert-info'
                          : 'alert-warning'
                      }`}>
                        <div className="flex-1">
                          <h3 className="font-bold">
                            {membershipStatus.cancellation_request.status === 'processed' 
                              ? '구독 취소 승인 완료'
                              : membershipStatus.cancellation_request.status === 'rejected'
                              ? '구독 취소 신청 거부됨'
                              : '구독 취소 신청 진행 중'}
                          </h3>
                          <div className="text-sm">
                            <p>신청일: {formatDate(membershipStatus.cancellation_request.requested_at)}</p>
                            {membershipStatus.cancellation_request.processed_at && (
                              <p>처리일: {formatDate(membershipStatus.cancellation_request.processed_at)}</p>
                            )}
                            <p>가입 유형: {membershipStatus.cancellation_request.cancellation_type_display || membershipStatus.cancellation_request.cancellation_type}</p>
                            {membershipStatus.cancellation_request.reason && (
                              <div className={membershipStatus.cancellation_request.status === 'rejected' ? 'mt-3' : ''}>
                                {membershipStatus.cancellation_request.status === 'rejected' ? (
                                  <>
                                    <p className="font-semibold">사유:</p>
                                    <div className="bg-red-50 mt-2 p-3 rounded-sm border border-red-200 text-sm">
                                      {membershipStatus.cancellation_request.reason}
                                    </div>
                                  </>
                                ) : (
                                  <p>사유: {membershipStatus.cancellation_request.reason}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-ghost absolute top-2 right-2 p-1 min-h-0 h-6 w-6"
                          onClick={handleHideCancellationNotice}
                          title="알림 닫기"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })()}

                  <div className="divider"></div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      className="btn btn-primary flex-1 py-2"
                      onClick={handleUpgrade}
                    >
                      플랜 변경/업그레이드
                    </button>
                    
                    {membershipStatus.membership.cancel_planned ? (
                      // 구독 취소 예정 상태일 때는 철회 버튼 표시
                      <button
                        className="btn btn-outline btn-success flex-1 py-2"
                        onClick={handleWithdrawCancellation}
                      >
                        구독 취소 철회 (재개)
                      </button>
                    ) : membershipStatus.cancellation_request ? (
                      membershipStatus.cancellation_request.status === 'pending' ? (
                        <button
                          className="btn btn-outline btn-warning flex-1 py-2"
                          onClick={handleWithdrawCancellation}
                        >
                          구독 취소 신청 철회
                        </button>
                      ) : membershipStatus.cancellation_request.status === 'processed' ? (
                        <button
                          className="btn btn-disabled flex-1 py-2"
                          disabled
                        >
                          구독 취소 처리 완료
                        </button>
                      ) : membershipStatus.cancellation_request.status === 'rejected' ? (
                        <button
                          className="btn btn-outline btn-error flex-1 py-2"
                          onClick={handleCancelSubscription}
                        >
                          구독 취소 신청
                        </button>
                      ) : (
                        <button
                          className="btn btn-disabled flex-1 py-2"
                          disabled
                        >
                          처리 완료
                        </button>
                      )
                    ) : (
                      <button
                        className="btn btn-outline btn-error flex-1 py-2"
                        onClick={handleCancelSubscription}
                      >
                        구독 취소 신청
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📝</div>
                    <h2 className="text-2xl font-bold mb-4">활성 멤버십이 없습니다</h2>
                    <p className="text-lg text-base-content/70 mb-6">
                      황금독서클럽 멤버십에 가입하여 다양한 혜택을 누려보세요!
                    </p>
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={() => window.location.href = '/select_plan'}
                    >
                      멤버십 가입하기
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 문의 안내 박스 */}
          <div className="mb-8 p-4 border border-base-300 rounded-lg bg-base-50">
            <p className="text-sm text-base-content/80 text-center">
              가입, 가입취소, 환불 등에 대한 문의 사항은{' '}
              <a
                href="/ask"
                className="text-primary hover:text-primary-focus underline font-medium"
              >
                1:1문의 게시판
              </a>
              에 올려주시면 감사하겠습니다.
            </p>
          </div>

          {/* 멤버십 혜택 안내 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="card bg-base-100 shadow-md">
              <div className="card-body text-center">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="font-bold mb-2">황금독서클럽</h3>
                <p className="text-sm text-base-content/70">
                  매달 선정도서와 함께하는 독서 모임
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-md">
              <div className="card-body text-center">
                <div className="text-4xl mb-4">📧</div>
                <h3 className="font-bold mb-2">뉴스레터</h3>
                <p className="text-sm text-base-content/70">
                  월간 뉴스레터 우편 발송
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-md">
              <div className="card-body text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="font-bold mb-2">온라인 클래스</h3>
                <p className="text-sm text-base-content/70">
                  온라인/오프라인 특강 참석
                </p>
              </div>
            </div>
          </div>

          {/* 황금독서클럽 카카오채널 배너 */}
          <div className="w-full mb-8">
            <a
              href="https://pf.kakao.com/_HPSJn"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
            >
              <div className="bg-gradient-to-r from-yellow-300 to-yellow-400 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 text-center">
                <div className="flex items-center justify-center gap-4">
                  <div className="text-4xl">💬</div>
                  <div className="text-black">
                    <h3 className="text-xl font-bold mb-1">황금독서클럽 카카오채널</h3>
                    <p className="text-sm opacity-80">독서클럽 소식과 정보를 카카오톡으로 받아보세요!</p>
                  </div>
                  <div className="text-2xl">→</div>
                </div>
              </div>
            </a>
          </div>

          {/* 결제 내역 (추후 구현) */}
          {/* <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <h3 className="card-title mb-4">결제 내역</h3>
              <div className="text-center py-8 text-base-content/50">
                <p>결제 내역이 없습니다.</p>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}
